import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import {
  SupplierCategorySchema,
  SupplierSchema,
  type SupplierCategoryDto,
  type SupplierDto,
} from '@taqon/contracts';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { getData, getPage, patchJson, postJson } from '../../shared/api/client';
import { useUrlList } from '../../shared/ui/url-list';
import { hasCapability, useSession } from '../../shared/session/session';
import { SearchFilterBar } from '../../shared/ui/filters';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { ExportButton } from '../../shared/export/ExportButton';
import { ImportButton } from '../../shared/import/ImportButton';
import { exportHref } from '../../shared/export/export-href';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';

/**
 * The supplier master (FRD-PRC-007 to 014). Creating and changing a supplier are shown only with
 * their permission (FRD-PRC-013); the API enforces them regardless (SDD §22.4). The Supplier ID
 * is generated (FRD-CORE-035), duplicates are refused by the configured matching rules
 * (FRD-PRC-014) and the category is a value of the supplier category list (ERD §7.2).
 *
 * The TRN and the bank details — bank, account name and number — are sensitive (FRD-PRC-010,
 * FRD-USR-015; ERD §7.2) and designated for masking, so most people see them masked. A masked
 * value is never shown, pre-filled or sent back: the field starts empty and is sent only when a
 * new value is typed.
 * Unmasked values reach only a holder of `procurement.supplier:view_unmasked`, through the
 * audited masking path (FRD-USR-017).
 */
export function SuppliersPage() {
  const session = useSession();
  const canCreate = hasCapability(session.data, 'procurement.supplier:create');
  const canChange = hasCapability(session.data, 'procurement.supplier:change');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<string | null>(null);
  const [editing, setEditing] = useState<SupplierDto | null>(null);
  // Search, status, category and the page live in the URL (FRD-CORE-038; a dashboard figure can
  // link to `/suppliers?status=active`). The server answers every filter and page.
  const list = useUrlList();
  const params = list.apiParams(
    { search: 'search', status: 'status', category: 'category_code' },
    'code',
  );
  const categories = useSupplierCategories();

  const suppliers = useQuery({
    queryKey: ['suppliers', params.toString()],
    queryFn: () => getPage(`${SUPPLIERS}?${params.toString()}`, SupplierSchema),
  });
  const exportParams = new URLSearchParams(params);
  exportParams.delete('page');
  exportParams.delete('pageSize');

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Suppliers"
        description="The supplier master. TRN and bank details are masked unless you are permitted to see them."
        actions={
          <>
            <ExportButton
              recordType="procurement.supplier"
              href={exportHref(SUPPLIERS, exportParams)}
            />
            <ImportButton recordType="procurement.supplier" />
            {canCreate && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={(event) => {
                  // The dialog returns focus here when it closes.
                  event.currentTarget.focus();
                  setCreated(null);
                  setCreating(true);
                }}
              >
                New supplier
              </Button>
            )}
          </>
        }
      />
      {created !== null && (
        <Alert
          severity="success"
          onClose={() => {
            setCreated(null);
          }}
        >
          Supplier {created} created.
        </Alert>
      )}
      <Box role="search" aria-label="Filter suppliers">
        <SearchFilterBar
          label="Search Supplier ID or name"
          value={list.get('search')}
          onChange={(value) => {
            list.set('search', value);
          }}
        >
          <TextField
            select
            size="small"
            label="Status"
            value={list.get('status')}
            onChange={(e) => {
              list.set('status', e.target.value);
            }}
            slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            label="Category"
            value={list.get('category')}
            onChange={(e) => {
              list.set('category', e.target.value);
            }}
            slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
          >
            <MenuItem value="">Any</MenuItem>
            {categoryOptions(categories.data ?? [], list.get('category')).map((option) => (
              <MenuItem key={option.valueCode} value={option.valueCode}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </SearchFilterBar>
      </Box>
      {suppliers.isError && <ErrorState message={suppliers.error.message} />}
      <Section title="Suppliers">
        {suppliers.isPending && <LoadingState label="Loading suppliers" />}
        {suppliers.data?.items.length === 0 && <EmptyState title="No suppliers found" />}
        {suppliers.data !== undefined && suppliers.data.items.length > 0 && (
          <>
            <Table size="small" aria-label="Suppliers">
              <TableHead>
                <TableRow>
                  <TableCell>Supplier ID</TableCell>
                  <TableCell>Company name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Status</TableCell>
                  {canChange && <TableCell align="right">Action</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {suppliers.data.items.map((supplier) => (
                  <TableRow key={supplier.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Link
                        component={RouterLink}
                        to={`/suppliers/${supplier.id}`}
                        sx={{ fontWeight: 600 }}
                      >
                        {supplier.code}
                      </Link>
                    </TableCell>
                    <TableCell>{supplier.companyName}</TableCell>
                    <TableCell>
                      {categoryLabel(categories.data ?? [], supplier.categoryCode)}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={supplier.status} />
                    </TableCell>
                    {canChange && (
                      <TableCell align="right">
                        <Button
                          size="small"
                          onClick={(event) => {
                            event.currentTarget.focus();
                            setEditing(supplier);
                          }}
                        >
                          Edit
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={suppliers.data.total}
              page={list.page}
              rowsPerPage={list.pageSize}
              rowsPerPageOptions={[25, 50, 100]}
              onPageChange={(_, next) => {
                list.setPage(next);
              }}
              onRowsPerPageChange={(e) => {
                list.setPageSize(Number.parseInt(e.target.value, 10));
              }}
            />
          </>
        )}
      </Section>
      {creating && (
        <SupplierDialog
          onClose={(code) => {
            setCreating(false);
            if (code !== undefined) setCreated(code);
          }}
        />
      )}
      {editing !== null && (
        <SupplierDialog
          key={`${editing.id}:${String(editing.version)}`}
          supplier={editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
    </Stack>
  );
}

const SUPPLIERS = '/api/v1/procurement/suppliers';

/** The supplier category list (ERD §7.2), inactive values included so stored codes keep a label. */
export function useSupplierCategories() {
  return useQuery({
    queryKey: ['procurement', 'supplier-categories'],
    queryFn: () =>
      getData('/api/v1/procurement/supplier-categories', z.array(SupplierCategorySchema)),
  });
}

/** A stored category as people read it: its label, or the code when the list does not hold it. */
export function categoryLabel(
  categories: readonly SupplierCategoryDto[],
  code: string | null,
): string {
  if (code === null) return '—';
  const value = categories.find((c) => c.valueCode === code);
  if (value === undefined) return code;
  return value.active ? value.label : `${value.label} (retired)`;
}

/** The active values, plus the current one when it is retired, so it stays shown and selectable. */
function categoryOptions(
  categories: readonly SupplierCategoryDto[],
  current: string,
): readonly SupplierCategoryDto[] {
  return categories.filter((c) => c.active || c.valueCode === current);
}

/** A value the masking service replaced (SDD §10.3). It can be neither shown nor edited. */
export function isMasked(value: string | null): boolean {
  return value?.startsWith('••••') ?? false;
}

const TEXT_FIELDS = [
  ['companyName', 'Company name'],
  ['contactName', 'Contact name'],
  ['contactEmail', 'Contact email'],
  ['contactPhone', 'Contact phone'],
  ['addressLine1', 'Address'],
  ['city', 'City'],
  ['country', 'Country'],
] as const;

/** Designated sensitive (FRD-PRC-010): masked for most people, never re-sent when masked. */
const SENSITIVE_FIELDS = [
  ['trn', 'TRN'],
  ['bankName', 'Bank name'],
  ['bankAccountName', 'Bank account name'],
  ['bankAccountNumber', 'Bank account number'],
] as const;

type Field =
  (typeof TEXT_FIELDS)[number][0] | (typeof SENSITIVE_FIELDS)[number][0] | 'categoryCode';
const ALL_FIELDS: readonly Field[] = [
  ...TEXT_FIELDS.map(([field]) => field),
  'categoryCode',
  ...SENSITIVE_FIELDS.map(([field]) => field),
];

function valuesOf(supplier: SupplierDto | undefined): Record<Field, string> {
  return Object.fromEntries(
    ALL_FIELDS.map((field) => {
      const value = supplier?.[field] ?? null;
      // A masked value is not a value: the form starts empty and sends nothing unless replaced.
      return [field, value === null || isMasked(value) ? '' : value];
    }),
  ) as Record<Field, string>;
}

/**
 * Create (no `supplier`) or change one supplier. A change sends only what changed — the API
 * refuses a no-op — with If-Match carrying the version read. An emptied optional field is sent as
 * null ("clear it"); a masked field left empty is not sent at all.
 */
export function SupplierDialog({
  supplier,
  onClose,
}: {
  supplier?: SupplierDto;
  onClose: (createdCode?: string) => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const creating = supplier === undefined;
  const original = valuesOf(supplier);
  const categories = useSupplierCategories();
  const [values, setValues] = useState<Record<Field, string>>(original);
  const [status, setStatus] = useState(supplier?.status ?? 'active');

  const body: Record<string, string | null> = {};
  for (const field of ALL_FIELDS) {
    const value = values[field].trim();
    if (creating) {
      if (value !== '') body[field] = value;
    } else if (value !== original[field]) {
      body[field] = field === 'companyName' || value !== '' ? value : null;
    }
  }
  if (!creating && status !== supplier.status) body.status = status;
  const canSubmit = values.companyName.trim() !== '' && (creating || Object.keys(body).length > 0);

  const save = useMutation({
    mutationFn: () =>
      creating
        ? postJson(
            '/api/v1/procurement/suppliers',
            body,
            SupplierSchema,
            session.data?.csrfToken ?? '',
          )
        : patchJson(
            `/api/v1/procurement/suppliers/${supplier.id}`,
            body,
            SupplierSchema,
            session.data?.csrfToken ?? '',
            supplier.version,
          ),
    onSuccess: async (saved) => {
      onClose(creating ? saved.code : undefined);
      await queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      await queryClient.invalidateQueries({ queryKey: ['supplier'] });
    },
  });

  return (
    <FormDialog
      open
      title={creating ? 'New supplier' : `Edit supplier ${supplier.code}`}
      submitLabel={creating ? 'Create supplier' : 'Save changes'}
      canSubmit={canSubmit}
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={() => {
        onClose();
      }}
      onSubmit={() => {
        save.mutate();
      }}
    >
      {creating && (
        <Typography variant="body2" color="text.secondary">
          The Supplier ID is generated when the supplier is created.
        </Typography>
      )}
      {TEXT_FIELDS.map(([field, label]) => (
        <TextField
          key={field}
          label={label}
          required={field === 'companyName'}
          autoFocus={creating && field === 'companyName'}
          type={field === 'contactEmail' ? 'email' : 'text'}
          value={values[field]}
          onChange={(e) => {
            setValues((current) => ({ ...current, [field]: e.target.value }));
          }}
        />
      ))}
      <TextField
        select
        label="Category"
        value={values.categoryCode}
        onChange={(e) => {
          setValues((current) => ({ ...current, categoryCode: e.target.value }));
        }}
        helperText={
          categories.data !== undefined && categories.data.every((c) => !c.active)
            ? 'No supplier categories are configured yet (Administration › Lists).'
            : undefined
        }
      >
        <MenuItem value="">None</MenuItem>
        {categoryOptions(categories.data ?? [], values.categoryCode).map((option) => (
          <MenuItem key={option.valueCode} value={option.valueCode}>
            {option.active ? option.label : `${option.label} (retired)`}
          </MenuItem>
        ))}
        {values.categoryCode !== '' &&
          !(categories.data ?? []).some((c) => c.valueCode === values.categoryCode) && (
            <MenuItem value={values.categoryCode}>{values.categoryCode}</MenuItem>
          )}
      </TextField>
      <Divider textAlign="left">
        <Typography variant="subtitle2" component="h3">
          TRN &amp; bank details (sensitive)
        </Typography>
      </Divider>
      {SENSITIVE_FIELDS.map(([field, label]) => {
        const masked = isMasked(supplier?.[field] ?? null);
        return (
          <TextField
            key={field}
            label={label}
            value={values[field]}
            placeholder={masked ? '•••• masked' : undefined}
            helperText={masked ? 'Masked. Enter a value only to replace it.' : undefined}
            slotProps={{
              inputLabel: masked ? { shrink: true } : {},
              htmlInput: { autoComplete: 'off' },
            }}
            onChange={(e) => {
              setValues((current) => ({ ...current, [field]: e.target.value }));
            }}
          />
        );
      })}
      {!creating && (
        <TextField
          select
          label="Status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
          }}
        >
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
        </TextField>
      )}
    </FormDialog>
  );
}
